import { useState, useEffect } from 'react';
import { Save, Loader2 } from 'lucide-react';
import {
  AMBER, NAVY, BORDER_COLOR, BG_FIELD,
  bubbleStyle, navyBtn, fieldStyle,
} from '../../styles/tokens';
import client from '../../api/client';

const SETTING_GROUPS = [
  {
    title: 'Aprobadores PTO',
    description: 'Correos de los aprobadores en la cadena de aprobación de 5 niveles',
    keys: [
      { key: 'pto_approver_l3_email', label: 'Aprobador L3 (Administración)', placeholder: 'lisette@v2aconsulting.com' },
      { key: 'pto_approver_l4_email', label: 'Aprobador L4 (Recursos Humanos)', placeholder: 'rosa@v2aconsulting.com' },
      { key: 'pto_approver_l5_email', label: 'Aprobador L5 (Directora)',        placeholder: 'diana@v2aconsulting.com' },
    ],
  },
  {
    title: 'Recordatorios',
    description: 'Umbrales de tiempo para enviar recordatorios de aprobación pendiente',
    keys: [
      { key: 'pto_reminder_threshold_1', label: 'Primer recordatorio (horas)',  placeholder: '24' },
      { key: 'pto_reminder_threshold_2', label: 'Segundo recordatorio (horas)', placeholder: '48' },
      { key: 'pto_reminder_threshold_3', label: 'Tercer recordatorio (horas)',  placeholder: '72' },
    ],
  },
  {
    title: 'Correo O365 (SMTP)',
    description: 'Configuración de envío de correo vía Office 365',
    keys: [
      { key: 'o365_smtp_host',    label: 'Servidor SMTP',     placeholder: 'smtp.office365.com' },
      { key: 'o365_smtp_port',    label: 'Puerto',            placeholder: '587' },
      { key: 'o365_smtp_user',    label: 'Usuario (correo)',  placeholder: 'noreply@v2aconsulting.com' },
      { key: 'o365_smtp_pass',    label: 'Contraseña',        placeholder: '••••••••', type: 'password' },
      { key: 'o365_from_name',    label: 'Nombre remitente',  placeholder: 'V&A Platform' },
    ],
  },
  {
    title: 'Exportación Nómina (Sage)',
    description: 'Configuración del export CSV para Sage 50',
    keys: [
      {
        key: 'nomina_ticket_number_start',
        label: 'Número inicial de Ticket',
        placeholder: '90310',
        type: 'number',
        helper: 'El próximo export iniciará desde este número de forma secuencial.',
      },
    ],
  },
];

export default function AdminSettings() {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    client.get('/api/admin/settings')
      .then(r => {
        setSettings(r.data || {});
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  function handleChange(key, value) {
    setSaved(false);
    setSettings(prev => ({ ...prev, [key]: value }));
  }

  async function handleSave() {
    setSaving(true);
    try {
      await client.put('/api/admin/settings', settings);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      alert('Error al guardar: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>
        <Loader2 size={20} className="spin" /> Cargando configuración…
      </div>
    );
  }

  return (
    <div style={{ padding: '24px 28px', maxWidth: 800, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h2 style={{ fontSize: 20, fontWeight: '700', color: NAVY, margin: 0 }}>Configuración de la Plataforma</h2>
        <button onClick={handleSave} disabled={saving} style={{
          ...navyBtn, display: 'flex', alignItems: 'center', gap: 6,
          opacity: saving ? 0.6 : 1,
          background: saved ? '#16a34a' : AMBER,
        }}>
          <Save size={14} /> {saving ? 'Guardando…' : saved ? 'Guardado' : 'Guardar Cambios'}
        </button>
      </div>

      {SETTING_GROUPS.map(group => (
        <div key={group.title} style={{ ...bubbleStyle, marginBottom: 16 }}>
          <h3 style={{ fontSize: 15, fontWeight: '700', color: NAVY, marginBottom: 4 }}>{group.title}</h3>
          <p style={{ fontSize: 12, color: '#64748b', marginBottom: 16 }}>{group.description}</p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {group.keys.map(({ key, label, placeholder, type, helper }) => (
              <div key={key}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: '600', color: '#475569', marginBottom: 4 }}>
                  {label}
                </label>
                <input
                  type={type || 'text'}
                  value={settings[key] || ''}
                  onChange={e => handleChange(key, e.target.value)}
                  placeholder={placeholder}
                  style={fieldStyle}
                />
                {helper && (
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>{helper}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
